import React, { useEffect, useState } from 'react';
import { PopupDatePicker } from '@amzn/stencil-react-components/popup-date-picker';
import { FormWrapper } from '@amzn/stencil-react-components/form';
import { Input, InputWrapper } from '@amzn/stencil-react-components/input';
import { Radio } from '@amzn/stencil-react-components/radio';
import { Select } from '@amzn/stencil-react-components/select';

import { Col, Row, Spacer, View } from '@amzn/stencil-react-components/layout';
import { Label, Text } from '@amzn/stencil-react-components/text';
import { FormattedMessage, useIntl } from 'react-intl';
import { Frequency, TIMEZONES } from '../../enums/schedulingConstant';
import { useDispatch } from 'react-redux';
import { updateJourneySchedule } from '../../actions/journeyDetailsAction';
import { accessByValue } from '../../util/journey/journeyUtils';
import { FROM_ADDRESS_FOOTER_ERROR_TEXT, FROM_ADDRESS_FOOTER_TEXT } from '../../constants/messages';
import { campaignDateBuilder, validateEmail } from '../../util/campaign/campaignUtils';

const ScheduleForm = ({ data, id, readonly = false }) => {
  const dispatch = useDispatch();
  const { formatMessage } = useIntl();
  const { startTime, endTime, frequency, timeZone, fromAddress } = data;
  const timezoneOptions = TIMEZONES;
  const [senderAddressFooterText, setSenderAddressFooterText] = useState(FROM_ADDRESS_FOOTER_TEXT);
  const [fromEmail, setFromEmail] = useState(fromAddress);

  const [startTimeUTC, setStartTimeUTC] = useState(startTime);
  const [endTimeUTC, setEndTimeUTC] = useState(endTime);
  const [endTimeError, setEndTimeError] = useState('');
  const [startTimeError, setStartTimeError] = useState('');

  const timezoneValueAccessor = accessByValue;
  const timezoneRenderOption = option => (
    <Row key={`row-timezone-${option.id}`} gridGap="S200" alignItems="center">
      {option.label}
      <Text key={`text-timezone-${option.id}`} fontSize="T100" color="neutral70">
        {option.value ? `(${option.value})` : 'Timezone'}
      </Text>
    </Row>
  );

  const onStartTimeChange = async e => {
    if (e === undefined || e == "") {
      setStartTimeError('');
      setStartTimeUTC(e);
      const updatedData = {
        ...data,
        startTime: '',
      };
      await dispatch(updateJourneySchedule(id, updatedData));
      return;
    }
    const formattedTime = campaignDateBuilder(new Date(e).toISOString());
    const start = new Date(e).getTime() ?? 0;
    const current = new Date().getTime();
    setStartTimeUTC(e);
    if (start < current) {
      setStartTimeError('Invalid date. Start date cannot be a past date');
      const updatedData = {
        ...data,
        startTime: '',
      };
      await dispatch(updateJourneySchedule(id, updatedData));
      return;
    }
    setStartTimeError('');
    const updatedData = {
      ...data,
      startTime: formattedTime,
    };
    await dispatch(updateJourneySchedule(id, updatedData));
  };

  const onEndTimeChange = async e => {
    if (e === undefined || e == "") {
      setEndTimeError('');
      setEndTimeUTC(e);
      const updatedData = {
        ...data,
        endTime: '',
      };
      await dispatch(updateJourneySchedule(id, updatedData));
      return;
    }
    const formattedTime = campaignDateBuilder(new Date(e).toISOString());
    const end = new Date(e)?.getTime() ?? 0;
    const start = new Date(startTime)?.getTime() ?? 0;
    setEndTimeUTC(e);
    if (start > end) {
      setEndTimeError('Invalid date. End date should be after start date');
      const updatedData = {
        ...data,
        endTime: '',
      };
      await dispatch(updateJourneySchedule(id, updatedData));
      return;
    }
    setEndTimeError('');
    const updatedData = {
      ...data,
      endTime: formattedTime,
    };
    await dispatch(updateJourneySchedule(id, updatedData));
  };

  const onTimezoneChange = async e => {
    const updatedData = {
      ...data,
      timeZone: e?.value,
    };
    await dispatch(updateJourneySchedule(id, updatedData));
  };

  const onFrequencyChange = value => {
    const updatedData = {
      ...data,
      frequency: value,
    };
    dispatch(updateJourneySchedule(id, updatedData));
  };

  const onFromAddressChange = e => {
    setFromEmail(e.target.value);
    if (validateEmail(e.target.value)) {
      setSenderAddressFooterText(FROM_ADDRESS_FOOTER_TEXT);
      const updatedData = {
        ...data,
        fromAddress: e.target.value,
      };
      dispatch(updateJourneySchedule(id, updatedData));
    } else {
      setSenderAddressFooterText(FROM_ADDRESS_FOOTER_ERROR_TEXT);
      const updatedData = {
        ...data,
        fromAddress: '',
      };
      dispatch(updateJourneySchedule(id, updatedData));
    }
  };

  return (
    <>
      <Label fontSize="T300" fontWeight="bold">
        <FormattedMessage id="app.scheduleForm.label.schedule" defaultMessage="Schedule" />
      </Label>
      <Col>
        <InputWrapper
          maxWidth={300}
          id="journey-start-time"
          labelText={formatMessage({ id: 'app.scheduleForm.label.startTime' })}
          htmlFor="campaign-start-time"
          footer={startTimeError}
          error={!!startTimeError}
          data-testid="group-campaign-start-time"
        >
          {inputProps => (
            <PopupDatePicker
              onChange={e => onStartTimeChange(e)}
              isDateDisabled={()=> readonly}
              value={startTimeUTC}
              showTime={true}
              locale="iso8601"
              id="journey-start-time"
              dataTestId="group-campaign-start-time-input"
              {...inputProps}
            />
          )}
        </InputWrapper>
      </Col>
      <Spacer height={15} />
      <Col>
        <InputWrapper
          maxWidth={300}
          id="journey-end-time"
          footer={endTimeError}
          error={!!endTimeError}
          labelText={formatMessage({ id: 'app.scheduleForm.label.endTime' })}
          htmlFor="journey-end-time"
          data-testid="end-date"
        >
          {inputProps => (
            <PopupDatePicker
              onChange={e => onEndTimeChange(e)}
              isDateDisabled={()=> readonly}
              value={endTimeUTC}
              showTime={true}
              locale="iso8601"
              id="journey-end-time"
              dataTestId="group-campaign-end-time"
              {...inputProps}
            />
          )}
        </InputWrapper>
      </Col>
      <Spacer height={15} />
      <Col>
        <InputWrapper
          labelText={formatMessage({ id: 'app.scheduleForm.label.fromEmailAddress' })}
          fontSize={'T400'}
          type="email"
          id="sender-email-wrapper"
          placeholder={formatMessage({ id: 'app.scheduleForm.placeholder.fromEmailAddress' })}
          error={false}
          footer=""
        >
          {inputProps => (
            <Input
              {...inputProps}
              data-testid="sender-email-input"
              error={false}
              type="email"
              value={fromEmail}
              placeholder="Email"
              disabled={readonly}
              onChange={e => onFromAddressChange(e)}
            />
          )}
        </InputWrapper>
      </Col>
      <Spacer height={15} />
      <Col>
        <Label>
          <FormattedMessage id="app.scheduleForm.label.timezone" defaultMessage="Timezone" />
        </Label>
        <Select
          id="some-select"
          data-testid="timezone-picker"
          labelId="some-select-label"
          onChange={e => onTimezoneChange(e)}
          options={timezoneOptions}
          placeholder={formatMessage({ id: 'app.scheduleForm.label.timezone' })}
          disabled={readonly}
          valueAccessor={timezoneValueAccessor}
          optionToString={option => option.label}
          renderOption={timezoneRenderOption}
        />
      </Col>

      <Spacer height={15} />
      <Col display="flex" flexDirection="row">
        <Col>
          <Label fontSize="T400">
            <FormattedMessage id="app.schedule.label.frequency" defaultMessage="Frequency" />
          </Label>
          <Spacer height="S300" />
          <FormWrapper gridGap="15px">
            <View display="flex" alignItems="center">
              <Radio
                id="channel-type-email-once"
                data-testid="channel-type-once"
                name={`frequency-${id}`}
                disabled={readonly}
                checked={frequency === Frequency.ONCE}
                onChange={() => onFrequencyChange(Frequency.ONCE)}
              />
              <Spacer width={15} />
              <Label fontSize={'T300'} htmlFor="channel-type-email-once">
                {Frequency.ONCE}
              </Label>
            </View>
            <View display="flex" alignItems="center">
              <Radio
                id="channel-type-email-hourly"
                data-testid="channel-type-hourly"
                name={`frequency-${id}`}
                disabled={readonly}
                checked={frequency === Frequency.HOURLY}
                onChange={() => onFrequencyChange(Frequency.HOURLY)}
              />
              <Spacer width={15} />
              <Label fontSize={'T300'} htmlFor="channel-type-email-once">
                {Frequency.HOURLY}
              </Label>
            </View>
            <View display="flex" alignItems="center">
              <Radio
                id="channel-type-email-daily"
                name={`frequency-${id}`}
                data-testid="channel-type-daily"
                disabled={readonly}
                checked={frequency === Frequency.DAILY}
                onChange={() => onFrequencyChange(Frequency.DAILY)}
              />
              <Spacer width={15} />
              <Label fontSize={'T300'} htmlFor="channel-type-email-daily">
                {Frequency.DAILY}
              </Label>
            </View>
            <View display="flex" alignItems="center">
              <Radio
                id="channel-type-email-weekly"
                name={`frequency-${id}`}
                data-testid="channel-type-weekly"
                disabled={readonly}
                checked={frequency === Frequency.WEEKLY}
                onChange={() => onFrequencyChange(Frequency.WEEKLY)}
              />
              <Spacer width={15} />
              <Label fontSize={'T300'} htmlFor="channel-type-email-weekly">
                {Frequency.WEEKLY}
              </Label>
            </View>
            <View display="flex" alignItems="center">
              <Radio
                id="frequency-type-email-monthly"
                name={`frequency-${id}`}
                data-testid="channel-type-monthly"
                disabled={readonly}
                checked={frequency === Frequency.MONTHLY}
                onChange={() => onFrequencyChange(Frequency.MONTHLY)}
              />
              <Spacer width={15} />
              <Label fontSize={'T300'} htmlFor="channel-type-email-monthly">
                {Frequency.MONTHLY}
              </Label>
            </View>
          </FormWrapper>
        </Col>
      </Col>
    </>
  );
};

export default ScheduleForm;
